import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { RootState, AppDispatch } from '../../app/store';
import api from '../../config/api';
import {
  fetchPostsAnalytics,
  fetchEngagementMetrics,
  fetchHashtagAnalytics,
  fetchCommentsAnalytics,
  fetchSentimentAnalytics,
  fetchSentimentTimeSeries,
  fetchCommenterAnalytics,
  fetchTopCommenters,
  fetchPostOwnerAnalytics,
  TimeSeriesData,
  EngagementData,
  HashtagData,
  SentimentData,
  SentimentTimeSeriesData,
  CommenterTimeSeriesData,
  TopCommenterData,
} from '../../features/analytics/analyticsSlice';
import dayjs, { Dayjs } from 'dayjs';
import {
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  SentimentSatisfiedAlt as PositiveIcon,
  SentimentNeutral as NeutralIcon,
  SentimentVeryDissatisfied as NegativeIcon,
  Flag as PriorityIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AnalyticsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useSelector((state: RootState) => state.analytics);
  const auth = useSelector((state: RootState) => state.auth);
  const theme = useTheme();
  const [timeRange, setTimeRange] = React.useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [tabValue, setTabValue] = React.useState(0);
  const [engagementChartType, setEngagementChartType] = React.useState<'bar' | 'line'>('bar');
  const [sentimentChartType, setSentimentChartType] = React.useState<'pie' | 'bar'>('pie');
  const [igCommenterChartType, setIgCommenterChartType] = React.useState<'line' | 'bar'>('line');
  const [igPageChartType, setIgPageChartType] = React.useState<'bar' | 'line'>('bar');
  const [selectedOwner, setSelectedOwner] = React.useState<string>('all');
  const [sentimentSource, setSentimentSource] = React.useState<'all' | 'instagram' | 'google' | 'tiktok' | 'gcc'>('all');
  const [startDate, setStartDate] = React.useState<Dayjs | null>(dayjs().startOf('year'));
  const [endDate, setEndDate] = React.useState<Dayjs | null>(dayjs());
  const [selectedBrand, setSelectedBrand] = React.useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = React.useState<string>('');
  const [brands, setBrands] = React.useState<Array<{ brand_id: string; brand_name: string; platform: string; }>>([]);
  const [distinctBrands, setDistinctBrands] = React.useState<Array<{ brand_id: string; brand_name: string; }>>([]);
  const [platforms, setPlatforms] = React.useState<Array<string>>([]);
  const [userBrandAccess, setUserBrandAccess] = React.useState<string[]>([]);
  const [userHasAllBrandsAccess, setUserHasAllBrandsAccess] = React.useState<boolean>(false);

  React.useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await api.get('/feedback/brands');
        const brandsData = response.data.data as Array<{ brand_id: string; brand_name: string; platform: string }>;
        const hasAllBrandsAccess = response.data.hasAllBrandsAccess;
        
        setBrands(brandsData);
        setUserHasAllBrandsAccess(hasAllBrandsAccess);
        
        // Extract user's brand access from API response
        // The backend already filters brands based on user permissions
        const brandIds = brandsData.map(brand => brand.brand_id);
        setUserBrandAccess(brandIds);
        
        // Extract unique brands
        const uniqueBrands = Array.from(
          new Map(
            brandsData.map((brand: { brand_id: string; brand_name: string; platform: string }) => 
              [brand.brand_id, { brand_id: brand.brand_id, brand_name: brand.brand_name }]
            )
          ).values()
        ) as Array<{ brand_id: string; brand_name: string }>;
        setDistinctBrands(uniqueBrands);
        
        // If user only has access to one brand, auto-select it
        if (uniqueBrands.length === 1) {
          setSelectedBrand(uniqueBrands[0].brand_id);
        }
        
        // Extract unique platforms
        const uniquePlatforms = Array.from(
          new Set(brandsData.map((brand: { platform: string }) => brand.platform))
        ) as string[];
        setPlatforms(uniquePlatforms);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    void fetchBrands();
  }, []);

  const fetchAnalytics = React.useCallback(() => {
    const params = {
      startDate: startDate?.toISOString() || dayjs().startOf('year').toISOString(),
      endDate: endDate?.toISOString() || dayjs().toISOString(),
      timeRange,
      brand_id: selectedBrand,
      platform: selectedPlatform
    };

    console.log('Fetching analytics with params:', params);
    
    dispatch(fetchPostsAnalytics(params));
    dispatch(fetchEngagementMetrics({ ...params }));
    dispatch(fetchHashtagAnalytics({ ...params }));
    dispatch(fetchCommentsAnalytics(params));
    dispatch(fetchSentimentAnalytics({ ...params }));
    dispatch(fetchSentimentTimeSeries(params));
    dispatch(fetchCommenterAnalytics(params));
    dispatch(fetchTopCommenters({ ...params, limit: 10 }));
    dispatch(fetchPostOwnerAnalytics(params));
  }, [dispatch, timeRange, startDate, endDate, selectedBrand, selectedPlatform]);

  React.useEffect(() => {
    console.log('Current analytics state:', analytics);
    console.log('Sentiment time series data:', analytics.sentimentTimeSeries);
  }, [analytics]);

  React.useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  React.useEffect(() => {
    if (analytics.sentimentTimeSeries.length > 0) {
      console.log('Sentiment Time Series Data:', analytics.sentimentTimeSeries);
      
      // Check if we have Google data
      const googleData = analytics.sentimentTimeSeries.filter(item => item.source === 'google');
      console.log('Google sentiment data:', googleData);
      
      // Check if we have GCC data
      const gccData = analytics.sentimentTimeSeries.filter(item => item.source === 'gcc');
      console.log('GCC sentiment data:', gccData);
      
      // Debug positive sentiment data
      const positiveData = Object.entries(analytics.sentimentTimeSeries
        .reduce((acc: { [key: string]: number }, item: SentimentTimeSeriesData) => {
          const date = new Date(item.period).toLocaleDateString();
          if (!acc[date]) {
            acc[date] = 0;
          }
          if (sentimentSource === 'all') {
            acc[date] += Number(item.positive_count || 0);
          } else if (item.source === sentimentSource) {
            acc[date] += Number(item.positive_count || 0);
          }
          return acc;
        }, {}))
        .map(([date, value]) => ({
          x: date,
          y: value,
        }))
        .filter(point => point.y > 0);
      
      console.log('Positive Sentiment Data Points:', positiveData);
    }
  }, [analytics.sentimentTimeSeries, sentimentSource]);

  const engagementData = React.useMemo(() => {
    return analytics.postsTimeSeries.map((item: TimeSeriesData) => ({
      date: new Date(item.period).toLocaleDateString(),
      posts: item.post_count,
      likes: item.total_likes,
      comments: item.total_comments,
    }));
  }, [analytics.postsTimeSeries]);

  const sentimentData = React.useMemo(() => {
    if (sentimentSource === 'all') {
      // Combine counts for both sources
      const combinedData = analytics.sentimentData.reduce((acc: { [key: string]: number }, item: SentimentData) => {
        if (!acc[item.sentiment]) {
          acc[item.sentiment] = 0;
        }
        acc[item.sentiment] += Number(item.count);
        return acc;
      }, {});

      return Object.entries(combinedData)
        .sort(([a], [b]) => {
          const sentimentOrder = ['positive', 'neutral', 'negative'];
          return sentimentOrder.indexOf(a) - sentimentOrder.indexOf(b);
        })
        .map(([sentiment, count]) => ({
          id: sentiment,
          label: sentiment,
          value: count,
          sentiment: sentiment,
          source: 'all'
        }));
    }

    // Filter by source if specific source is selected
    return analytics.sentimentData
      .filter((item: SentimentData) => item.source === sentimentSource)
      .sort((a, b) => {
        const sentimentOrder = ['positive', 'neutral', 'negative'];
        return sentimentOrder.indexOf(a.sentiment) - sentimentOrder.indexOf(b.sentiment);
      })
      .map((item: SentimentData) => ({
        id: item.sentiment,
        label: item.sentiment,
        value: Number(item.count),
        sentiment: item.sentiment,
        source: item.source,
      }));
  }, [analytics.sentimentData, sentimentSource]);

  const sentimentTimeSeriesData = React.useMemo(() => {
    console.log('Raw sentiment time series data:', analytics.sentimentTimeSeries);
    console.log('Current sentiment source:', sentimentSource);
    
    // First filter by source and date range
    const filteredData = analytics.sentimentTimeSeries
      .filter(item => {
        // Filter by source
        const sourceMatch = sentimentSource === 'all' || item.source === sentimentSource;
        
        // Filter by date range if dates are provided
        const itemDate = new Date(item.period);
        const startDateObj = startDate ? new Date(startDate.format('YYYY-MM-DD')) : null;
        const endDateObj = endDate ? new Date(endDate.format('YYYY-MM-DD')) : null;
        
        const dateInRange = (!startDateObj || itemDate >= startDateObj) && 
                           (!endDateObj || itemDate <= endDateObj);
        
        return sourceMatch && dateInRange;
      });
    
    // If 'all' sources selected, aggregate data by date
    if (sentimentSource === 'all') {
      // Create a map to aggregate sentiment counts by date
      const aggregatedByDate = filteredData.reduce((acc: { [key: string]: { positive: number, neutral: number, negative: number } }, item) => {
        const date = new Date(item.period).toLocaleDateString();
        
        if (!acc[date]) {
          acc[date] = { positive: 0, neutral: 0, negative: 0 };
        }
        
        // Sum the sentiment counts
        acc[date].positive += Number(item.positive_count) || 0;
        acc[date].neutral += Number(item.neutral_count) || 0;
        acc[date].negative += Number(item.negative_count) || 0;
        
        return acc;
      }, {});
      
      // Convert the map back to an array
      return Object.entries(aggregatedByDate).map(([date, counts]) => ({
        date,
        source: 'all',
        positive: counts.positive,
        neutral: counts.neutral,
        negative: counts.negative
      }));
    }
    
    // For a specific source, just map the data
    return filteredData.map((item: SentimentTimeSeriesData) => ({
      date: new Date(item.period).toLocaleDateString(),
      source: item.source,
      positive: Number(item.positive_count) || 0,
      neutral: Number(item.neutral_count) || 0,
      negative: Number(item.negative_count) || 0
    }));
      
  }, [analytics.sentimentTimeSeries, sentimentSource, startDate, endDate]);

  if (analytics.loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show no access message if user has no brands available
  if (distinctBrands.length === 0) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <Typography variant="h5" color="error" gutterBottom>
          No Brand Access
        </Typography>
        <Typography variant="body1">
          You don't have access to any brands. Please contact your administrator to request access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Analytics Dashboard
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Post Analytics" />
          <Tab label="Sentiment Analytics" />
          <Tab label="Hashtag Analytics" />
          <Tab label="IG Commenter Analytics" />
          <Tab label="IG Page Analytics" />
        </Tabs>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2}>
          <FormControl>
            <InputLabel>Brand</InputLabel>
            <Select
              value={selectedBrand}
              label="Brand"
              onChange={(e) => setSelectedBrand(e.target.value)}
              sx={{ minWidth: 200 }}
              disabled={!userHasAllBrandsAccess && distinctBrands.length === 1}
              startAdornment={
                userHasAllBrandsAccess ? (
                  <Tooltip title="You have access to all brands">
                    <Box sx={{ color: 'success.main', mr: 1, display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '1.2rem' }}>•</span>
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title="You have access to specific brands only">
                    <Box sx={{ color: 'warning.main', mr: 1, display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '1.2rem' }}>•</span>
                    </Box>
                  </Tooltip>
                )
              }
            >
              {userHasAllBrandsAccess && <MenuItem value="">All Brands</MenuItem>}
              {distinctBrands.map((brand) => (
                <MenuItem key={brand.brand_id} value={brand.brand_id}>
                  {brand.brand_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Platform</InputLabel>
            <Select
              value={selectedPlatform}
              label="Platform"
              onChange={(e) => setSelectedPlatform(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Platforms</MenuItem>
              {platforms.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value as 'daily' | 'weekly' | 'monthly')}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
              minDate={startDate || undefined}
            />
          </LocalizationProvider>
        </Stack>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Engagement Over Time
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Chart Type</InputLabel>
                    <Select
                      value={engagementChartType}
                      label="Chart Type"
                      onChange={(e) => setEngagementChartType(e.target.value as 'bar' | 'line')}
                    >
                      <MenuItem value="bar">Bar Chart</MenuItem>
                      <MenuItem value="line">Line Chart</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ height: 400 }}>
                  {engagementChartType === 'bar' ? (
                    <ResponsiveBar
                      data={engagementData}
                      keys={['posts', 'likes', 'comments']}
                      indexBy="date"
                      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={{ scheme: 'nivo' }}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Date',
                        legendPosition: 'middle',
                        legendOffset: 40,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Count',
                        legendPosition: 'middle',
                        legendOffset: -40,
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      legends={[
                        {
                          dataFrom: 'keys',
                          anchor: 'bottom-right',
                          direction: 'column',
                          justify: false,
                          translateX: 120,
                          translateY: 0,
                          itemsSpacing: 2,
                          itemWidth: 100,
                          itemHeight: 20,
                          itemDirection: 'left-to-right',
                          itemOpacity: 0.85,
                          symbolSize: 20,
                        },
                      ]}
                    />
                  ) : (
                    <ResponsiveLine
                      data={[
                        {
                          id: 'Posts',
                          data: engagementData.map(d => ({ x: d.date, y: d.posts }))
                        },
                        {
                          id: 'Likes',
                          data: engagementData.map(d => ({ x: d.date, y: d.likes }))
                        },
                        {
                          id: 'Comments',
                          data: engagementData.map(d => ({ x: d.date, y: d.comments }))
                        }
                      ]}
                      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                      xScale={{ type: 'point' }}
                      yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Date',
                        legendOffset: 40,
                        legendPosition: 'middle'
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Count',
                        legendOffset: -40,
                        legendPosition: 'middle'
                      }}
                      pointSize={10}
                      pointColor={{ theme: 'background' }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: 'serieColor' }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                      legends={[
                        {
                          anchor: 'bottom-right',
                          direction: 'column',
                          justify: false,
                          translateX: 100,
                          translateY: 0,
                          itemsSpacing: 0,
                          itemDirection: 'left-to-right',
                          itemWidth: 80,
                          itemHeight: 20,
                          itemOpacity: 0.75,
                          symbolSize: 12,
                          symbolShape: 'circle'
                        }
                      ]}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Post Type Distribution
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Posts</TableCell>
                        <TableCell align="right">Avg. Likes</TableCell>
                        <TableCell align="right">Avg. Comments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.engagement.map((row: EngagementData) => (
                        <TableRow key={row.post_type}>
                          <TableCell component="th" scope="row">
                            {row.post_type}
                          </TableCell>
                          <TableCell align="right">{row.total_posts}</TableCell>
                          <TableCell align="right">{Math.round(row.avg_likes)}</TableCell>
                          <TableCell align="right">{Math.round(row.avg_comments)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Sentiment Over Time
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={sentimentSource}
                        label="Source"
                        onChange={(e) => setSentimentSource(e.target.value as 'all' | 'instagram' | 'google' | 'tiktok' | 'gcc')}
                      >
                        <MenuItem value="all">All Sources</MenuItem>
                        <MenuItem value="instagram">Instagram</MenuItem>
                        <MenuItem value="google">Google Reviews</MenuItem>
                        <MenuItem value="tiktok">TikTok</MenuItem>
                        <MenuItem value="gcc">Guest Comment Cards</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
                <Box sx={{ height: 400 }}>
                  {analytics.sentimentTimeSeries.length > 0 ? (
                    <ResponsiveLine
                      data={[
                        {
                          id: 'Positive',
                          color: theme.palette.success.main,
                          data: sentimentTimeSeriesData
                            .filter(d => {
                              console.log('IG Page - Filtering positive data point:', d);
                              return d.positive > 0;
                            })
                            .map(d => ({ x: d.date, y: d.positive }))
                        },
                        {
                          id: 'Neutral',
                          color: theme.palette.grey[500],
                          data: sentimentTimeSeriesData
                            .filter(d => {
                              console.log('IG Page - Filtering neutral data point:', d);
                              return d.neutral > 0;
                            })
                            .map(d => ({ x: d.date, y: d.neutral }))
                        },
                        {
                          id: 'Negative',
                          color: theme.palette.error.main,
                          data: sentimentTimeSeriesData
                            .filter(d => {
                              console.log('IG Page - Filtering negative data point:', d);
                              return d.negative > 0;
                            })
                            .map(d => ({ x: d.date, y: d.negative }))
                        }
                      ].filter(series => {
                        console.log('IG Page - Series data length:', series.id, series.data.length);
                        return series.data.length > 0;
                      })}
                      margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                      xScale={{ type: 'point' }}
                      yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                      colors={{ datum: 'color' }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Date',
                        legendOffset: 40,
                        legendPosition: 'middle',
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Count',
                        legendOffset: -40,
                        legendPosition: 'middle',
                      }}
                      pointSize={10}
                      pointColor={{ theme: 'background' }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: 'color' }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                      enableSlices="x"
                      legends={[
                        {
                          anchor: 'bottom-right',
                          direction: 'column',
                          justify: false,
                          translateX: 100,
                          translateY: 0,
                          itemsSpacing: 0,
                          itemDirection: 'left-to-right',
                          itemWidth: 80,
                          itemHeight: 20,
                          itemOpacity: 0.75,
                          symbolSize: 12,
                          symbolShape: 'circle'
                        },
                      ]}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        No sentiment data available for the selected time period and source.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Sentiment Distribution
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Chart Type</InputLabel>
                    <Select
                      value={sentimentChartType}
                      label="Chart Type"
                      onChange={(e) => setSentimentChartType(e.target.value as 'pie' | 'bar')}
                    >
                      <MenuItem value="pie">Pie Chart</MenuItem>
                      <MenuItem value="bar">Bar Chart</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ height: 400 }}>
                  {sentimentChartType === 'pie' ? (
                    <ResponsivePie
                      data={sentimentData
                        .filter(item => sentimentSource === 'all' || item.source === sentimentSource)
                        .map(item => ({
                          id: item.sentiment,
                          label: item.sentiment,
                          value: Number(item.value)
                        }))}
                      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={{ scheme: 'set3' }}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor="#333333"
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                      legends={[
                        {
                          anchor: 'bottom',
                          direction: 'row',
                          justify: false,
                          translateX: 0,
                          translateY: 56,
                          itemsSpacing: 0,
                          itemWidth: 100,
                          itemHeight: 18,
                          itemTextColor: '#999',
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 18,
                          symbolShape: 'circle',
                        }
                      ]}
                    />
                  ) : (
                    <ResponsiveBar
                      data={sentimentData.filter(item => 
                        sentimentSource === 'all' || item.source === sentimentSource
                      )}
                      keys={['value']}
                      indexBy="id"
                      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={{ scheme: 'nivo' }}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Sentiment',
                        legendPosition: 'middle',
                        legendOffset: 32
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Count',
                        legendPosition: 'middle',
                        legendOffset: -40
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comment Response Stats
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell>Sentiment</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Replied</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.sentimentData
                        .filter(row => sentimentSource === 'all' || row.source === sentimentSource)
                        .sort((a, b) => {
                          // Sort by platform first, then by sentiment
                          const platformOrder = ['instagram', 'google', 'tiktok', 'gcc'];
                          const platformA = platformOrder.indexOf(a.source);
                          const platformB = platformOrder.indexOf(b.source);
                          
                          if (platformA !== platformB) {
                            return platformA - platformB;
                          }
                          
                          // If same platform, sort by sentiment
                          const sentimentOrder = ['positive', 'neutral', 'negative'];
                          const sentimentA = sentimentOrder.indexOf(a.sentiment);
                          const sentimentB = sentimentOrder.indexOf(b.sentiment);
                          return sentimentA - sentimentB;
                        })
                        .map((row: SentimentData) => (
                          <TableRow key={`${row.source}-${row.sentiment}`}>
                            <TableCell>
                              {row.source === 'instagram' ? 'Instagram' : 
                               row.source === 'google' ? 'Google Reviews' :
                               row.source === 'tiktok' ? 'TikTok' :
                               row.source === 'gcc' ? 'Guest Comment Cards' : row.source}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {row.sentiment}
                            </TableCell>
                            <TableCell align="right">{row.count}</TableCell>
                            <TableCell align="right">{row.replied_count}</TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Hashtags
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Hashtag</TableCell>
                        <TableCell align="right">Usage Count</TableCell>
                        <TableCell align="right">Avg. Likes</TableCell>
                        <TableCell align="right">Avg. Comments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.hashtags.map((row: HashtagData) => (
                        <TableRow key={row.hashtag}>
                          <TableCell component="th" scope="row">
                            #{row.hashtag}
                          </TableCell>
                          <TableCell align="right">{row.usage_count}</TableCell>
                          <TableCell align="right">{Math.round(row.avg_likes)}</TableCell>
                          <TableCell align="right">{Math.round(row.avg_comments)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          {/* <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Commenter Activity Over Time
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Chart Type</InputLabel>
                    <Select
                      value={igCommenterChartType}
                      label="Chart Type"
                      onChange={(e) => setIgCommenterChartType(e.target.value as 'line' | 'bar')}
                    >
                      <MenuItem value="line">Line Chart</MenuItem>
                      <MenuItem value="bar">Bar Chart</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ height: 400 }}>
                  {igCommenterChartType === 'line' ? (
                    <ResponsiveLine
                      data={[
                        {
                          id: 'commenters',
                          data: analytics.commenterTimeSeries.map((item: CommenterTimeSeriesData) => ({
                            x: new Date(item.period).toLocaleDateString(),
                            y: item.unique_commenters,
                          })),
                        },
                      ]}
                      margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                      xScale={{ type: 'point' }}
                      yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
                      curve="monotoneX"
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Date',
                        legendOffset: 40,
                        legendPosition: 'middle',
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Unique Commenters',
                        legendOffset: -40,
                        legendPosition: 'middle',
                      }}
                      pointSize={10}
                      pointColor={{ theme: 'background' }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: 'serieColor' }}
                      pointLabelYOffset={-12}
                      useMesh={true}
                    />
                  ) : (
                    <ResponsiveBar
                      data={analytics.commenterTimeSeries.map((item: CommenterTimeSeriesData) => ({
                        date: new Date(item.period).toLocaleDateString(),
                        commenters: item.unique_commenters,
                      }))}
                      keys={['commenters']}
                      indexBy="date"
                      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                      padding={0.3}
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={{ scheme: 'nivo' }}
                      borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Date',
                        legendPosition: 'middle',
                        legendOffset: 40,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Unique Commenters',
                        legendPosition: 'middle',
                        legendOffset: -40,
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid> */}

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Commenters
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell align="right">Total Comments</TableCell>
                        <TableCell align="right">Unique Posts</TableCell>
                        <TableCell align="right">Avg. Likes</TableCell>
                        <TableCell align="right">Sentiment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.topCommenters.map((commenter: TopCommenterData) => (
                        <TableRow key={commenter.user_id}>
                          <TableCell>{commenter.username}</TableCell>
                          <TableCell align="right">{commenter.total_comments}</TableCell>
                          <TableCell align="right">{commenter.unique_posts_commented}</TableCell>
                          <TableCell align="right">
                            {Math.round(commenter.avg_likes_per_comment * 10) / 10}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Positive Comments">
                                <Chip
                                  size="small"
                                  color="success"
                                  label={`${Math.round((commenter.positive_comments / commenter.total_comments) * 100)}%`}
                                  title="Positive Sentiment"
                                />
                              </Tooltip>
                              <Tooltip title="Neutral Comments">
                                <Chip
                                  size="small"
                                  color="default"
                                  label={`${Math.round((commenter.neutral_comments / commenter.total_comments) * 100)}%`}
                                  title="Neutral Sentiment"
                                />
                              </Tooltip>
                              <Tooltip title="Negative Comments">
                                <Chip
                                  size="small"
                                  color="error"
                                  label={`${Math.round((commenter.negative_comments / commenter.total_comments) * 100)}%`}
                                  title="Negative Sentiment"
                                />
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Commenter Engagement Metrics
                </Typography>
                {analytics.commenterTimeSeries.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Active Commenters: {analytics.commenterTimeSeries[0].total_active_commenters}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Average Comments per User: {analytics.commenterTimeSeries[0].avg_comments_per_user}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Median Comments per User: {analytics.commenterTimeSeries[0].median_comments_per_user}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Users with Replies: {analytics.commenterTimeSeries[0].users_with_replies}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Replies: {analytics.commenterTimeSeries[0].total_replies}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Sentiment Trends Over Time
                  </Typography>
                </Box>
                <Box sx={{ height: 400 }}>
                  <ResponsiveLine
                    data={[
                      {
                        id: 'Positive',
                        color: theme.palette.success.main,
                        data: sentimentTimeSeriesData
                          .filter(d => d.positive > 0)
                          .map(d => ({ x: d.date, y: d.positive }))
                      },
                      {
                        id: 'Neutral',
                        color: theme.palette.grey[500],
                        data: sentimentTimeSeriesData
                          .filter(d => d.neutral > 0)
                          .map(d => ({ x: d.date, y: d.neutral }))
                      },
                      {
                        id: 'Negative',
                        color: theme.palette.error.main,
                        data: sentimentTimeSeriesData
                          .filter(d => d.negative > 0)
                          .map(d => ({ x: d.date, y: d.negative }))
                      }
                    ].filter(series => series.data.length > 0)}
                    curve="basis"
                    margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Date',
                      legendOffset: 36,
                      legendPosition: 'middle'
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Count',
                      legendOffset: -40,
                      legendPosition: 'middle'
                    }}
                    pointSize={10}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={true}
                    legends={[
                      {
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 100,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 80,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle'
                      }
                    ]}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Post Owner Performance
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Post Owner</TableCell>
                        <TableCell align="right">Total Comments</TableCell>
                        <TableCell align="right">Posts with Comments</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            <Typography variant="body2">Post Engagement</Typography>
                            <Chip size="small" color="primary" label="Post Likes" />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">Sentiment Distribution</TableCell>
                        <TableCell align="right">Priority Distribution</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.postOwnerAnalytics.map((row) => (
                        <TableRow key={row.owner_username}>
                          <TableCell component="th" scope="row">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {row.owner_username[0].toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">@{row.owner_username}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.owner_full_name}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Total Comments">
                              <Chip
                                icon={<CommentIcon />}
                                label={row.total_comments}
                                size="small"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{row.posts_with_comments}</TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                <React.Fragment>
                                  <Typography color="inherit" variant="subtitle2">Post Engagement Details {row.total_post_likes}</Typography>
                                  <Box component="div" sx={{ mt: 1 }}>
                                    <Typography variant="body2">• Total Post Likes: {row.total_post_likes || 0}</Typography>
                                    <Typography variant="body2">• Number of Posts: {row.posts_with_comments || 0}</Typography>
                                    <Typography variant="body2">• Avg. Likes Per Post: {
                                      Math.round((row.total_post_likes || 0) / (row.posts_with_comments || 1) * 10) / 10
                                    }</Typography>
                                  </Box>
                                </React.Fragment>
                              }
                            >
                              <Chip
                                icon={<ThumbUpIcon />}
                                label={
                                  (() => {
                                    // Calculate average post likes using total_post_likes from the API
                                    const totalPostLikes = row.total_post_likes || 0;
                                    const totalPosts = row.posts_with_comments || 1; // Use posts with comments as a proxy for total posts
                                    const avgLikes = (totalPostLikes / totalPosts).toFixed(2); // Round to 2 decimal places
                                    
                                    // Format with K suffix for thousands
                                    const formatNumber = (num: number) => {
                                      if (num >= 1000) {
                                        return (num / 1000).toFixed(2) + 'K';
                                      }
                                      return num.toString();
                                    };
                                    
                                    return `${formatNumber(parseFloat(avgLikes))} avg (${formatNumber(totalPostLikes)} total)`;
                                  })()
                                }
                                size="small"
                                color="primary"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Positive Comments">
                                <Chip
                                  icon={<PositiveIcon />}
                                  label={row.positive_comments}
                                  size="small"
                                  color="success"
                                />
                              </Tooltip>
                              <Tooltip title="Neutral Comments">
                                <Chip
                                  icon={<NeutralIcon />}
                                  label={row.neutral_comments}
                                  size="small"
                                  color="info"
                                />
                              </Tooltip>
                              <Tooltip title="Negative Comments">
                                <Chip
                                  icon={<NegativeIcon />}
                                  label={row.negative_comments}
                                  size="small"
                                  color="error"
                                />
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="High Priority">
                                <Chip
                                  icon={<PriorityIcon />}
                                  label={row.high_priority_comments}
                                  size="small"
                                  color="error"
                                />
                              </Tooltip>
                              <Tooltip title="Medium Priority">
                                <Chip
                                  icon={<PriorityIcon />}
                                  label={row.medium_priority_comments}
                                  size="small"
                                  color="warning"
                                />
                              </Tooltip>
                              <Tooltip title="Low Priority">
                                <Chip
                                  icon={<PriorityIcon />}
                                  label={row.low_priority_comments}
                                  size="small"
                                  color="info"
                                />
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default AnalyticsPage; 